import { X, Check, Loader2 } from "lucide-react"
import { Microphone } from 'iconoir-react';
import { useTheme } from "components/core/theme-provider"
import useAudioRecorder from "../hooks/use-audio-recorder";
import { useSpeechToText } from "../hooks/use-speech-to-text";
import { TranscribeAudioToTextResponse } from "../types/model.types";
// import { useCallback, useEffect, useRef } from "react";

interface DictateButtonProps {
    onTranscribe: (content: string) => void;
    setIsDictationOnGoing: (isDictationOnGoing: boolean) => void;
    isDictationOnGoing: boolean;
    disabled?: boolean;
}

interface DictateMicButtonProps {
    disabled: boolean;
    isPressed: boolean;
    isRecording?: boolean;
    onClick?: () => void;
    className?: string;
}

const DictateMicButton = ({
    disabled,
    isPressed,
    isRecording = false,
    onClick,
    className = "",
}: DictateMicButtonProps) => (
    <button
        aria-pressed={isPressed}
        aria-label="Dictate button"
        className={`flex h-8 min-w-8 items-center justify-center sm:p-2 p-1 hover:rounded-full hover:bg-muted disabled:opacity-40 ${isRecording ? 'animate-pulse-glow' : ''} ${className}`}
        onClick={onClick}
        disabled={disabled}
    >
        <Microphone
            width={20}
            height={20}
            strokeWidth={1.5}
            className="shrink-0 text-[#3D3D3D] dark:text-foreground"
        />
    </button>
)

export const DictateButton = ({
    onTranscribe,
    setIsDictationOnGoing,
    isDictationOnGoing,
    disabled = false,
}: DictateButtonProps) => {
    const { theme } = useTheme();
    const { isRecording, startRecording, stopRecording, cancelRecording, canvasRef } = useAudioRecorder();
    const { mutateAsync: transcribeAudioToText, isPending: isTranscribing } = useSpeechToText();

    const handleStartRecording = async () => {
        try {
            await startRecording();
            setIsDictationOnGoing(true);
        } catch (error) {
            console.log(error);
            setIsDictationOnGoing(false);
        }
    }

    const handleStopRecording = async () => {
        if (!isRecording) return;

        try {
            const audioBlob = await stopRecording() as Blob | null;
            if (audioBlob) {
                const res = await transcribeAudioToText(audioBlob) as TranscribeAudioToTextResponse;
                console.log(res);
                setIsDictationOnGoing(false);
                if (res) {
                    onTranscribe?.(res.text);
                } else {
                    console.warn('Transcription response missing text or has error:', res);
                }
            }
        } catch (error) {
            console.error('Error in handleStopRecording:', error);
            setIsDictationOnGoing(false);
        }
    }

    const handleCancelRecording = () => {
        setIsDictationOnGoing(false);
        cancelRecording();
    }

    if (isDictationOnGoing) {
        return (
            <div className="flex w-full min-w-0 items-center gap-1 pl-0">
                <DictateMicButton
                    disabled={isTranscribing || disabled}
                    isPressed={true}
                    className={`${theme === 'dark' ? 'border-black-200 opacity-70' : 'border-black-300 opacity-70'} cursor-default`}
                />
                <div className="min-w-0 flex-2">
                    <canvas ref={canvasRef} height={32} className="w-full rounded" />
                </div>

                <button
                    aria-pressed="true"
                    aria-label="Cancel recording button"
                    className={
                        `flex h-[34px] min-w-[34px] items-center justify-center sm:p-2 p-1 rounded-full border-[0.5px] ${theme === 'dark' ? 'border-black-500' : 'border-black-500'} shadow-sm hover:bg-muted disabled:opacity-40`
                    }
                    disabled={isTranscribing || disabled}
                    onClick={handleCancelRecording}
                >
                    <X size={16} className={`${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                </button>

                {!isTranscribing && <button
                    aria-pressed="true"
                    aria-label="Stop recording button"
                    className={
                        'flex h-[34px] min-w-[34px] items-center justify-center sm:p-2 p-1 rounded-full border-[0.5px] border-black-500 shadow-sm hover:bg-muted disabled:opacity-30 '
                        // + (theme === 'dark' ? 'border-gray-500 opacity-70' : 'border-gray-200')
                    }
                    disabled={disabled}
                    onClick={handleStopRecording}
                >
                    <Check size={16} className={`${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                </button>}
                {isTranscribing && <button
                    aria-pressed="true"
                    aria-label="Transcribing loader button"
                    className={
                        `flex h-[34px] min-w-[34px] items-center justify-center sm:p-2 p-1 rounded-full border-[0.5px] ${theme === 'dark' ? 'border-black-500' : 'border-black-500'} shadow-sm hover:bg-muted disabled:opacity-30`
                    }
                >
                    <Loader2 className={`animate-spin ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                </button>}
            </div>
        )
    }

    return (
        <DictateMicButton
            isPressed={false}
            onClick={handleStartRecording}
            disabled={isTranscribing || disabled}
            isRecording={isRecording}
            className={`${theme === 'dark' ? 'border-black-500' : 'border-black-100'} ${isRecording ? 'bg-primary-50 border-transparent' : ''}`}
        />
    )
}
